import React from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, FolderOpen, Sparkles, Film, ClipboardList, ArrowRight } from "lucide-react";

function getGreeting(hour) {
  if (hour < 5) return "Still up";
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
}


export default function Dashboard() {
  const { user } = useAuth();
  const [projects, setProjects] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/projects");
        setProjects(data);
      } catch (e) {
        toast.error("Could not load projects");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const greet = getGreeting(new Date().getHours());

  return (
    <DashboardLayout>
      <div className="max-w-[1200px] mx-auto px-8 py-10">
        <div className="uppercase text-xs tracking-[0.3em] text-[#71717A] mb-4">{greet}</div>
        <h1 className="font-editorial text-4xl sm:text-5xl leading-tight mb-8" data-testid="dashboard-greeting">
          {greet}, {user?.name?.split(" ")[0] || "friend"}.<br />
          <span className="italic text-[#A1A1AA]">What are we creating?</span>
        </h1>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-[#1E1E1E] mt-10">
          {[
            { icon: Plus, label: "New script", to: "/app/projects?new=1", testId: "quick-new-script" },
            { icon: Sparkles, label: "Start with idea", to: "/app/projects?new=1", testId: "quick-idea" },
            { icon: Film, label: "Generate storyboard", to: "/app/projects", testId: "quick-storyboard" },
            { icon: ClipboardList, label: "Open Script Doctor", to: "/app/projects", testId: "quick-doctor" },
          ].map((q) => (
            <Link key={q.label} to={q.to}
              className="bg-[#0A0A0A] px-6 py-6 flex items-center gap-4 hover:bg-[#141414] transition-colors group"
              data-testid={q.testId}>
              <div className="w-10 h-10 border border-[#27272A] flex items-center justify-center group-hover:border-[#D4AF37] transition-colors">
                <q.icon size={16} strokeWidth={1.5} className="text-[#D4AF37]" />
              </div>
              <span className="text-sm">{q.label}</span>
              <ArrowRight size={14} className="ml-auto text-[#71717A] group-hover:text-[#D4AF37] transition-colors" />
            </Link>
          ))}
        </div>

        <div className="mt-14">
          <div className="flex items-baseline justify-between mb-6">
            <div>
              <div className="uppercase text-xs tracking-[0.3em] text-[#D4AF37] mb-2">Your projects</div>
              <h2 className="font-editorial text-3xl">Continue where you left off</h2>
            </div>
            <Link to="/app/projects" className="text-sm text-[#A1A1AA] hover:text-[#FFFFF0]" data-testid="see-all-projects">See all →</Link>
          </div>

          <ProjectsSection loading={loading} projects={projects} />
        </div>
      </div>
    </DashboardLayout>
  );
}

function ProjectsSection({ loading, projects }) {
  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[1, 2, 3].map((i) => <div key={i} className="h-40 bg-[#141414] border border-[#1E1E1E] animate-pulse" />)}
      </div>
    );
  }
  if (projects.length === 0) return <EmptyDashboard />;
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {projects.slice(0, 6).map((p) => <ProjectTile key={p.id} p={p} />)}
    </div>
  );
}

function EmptyDashboard() {
  return (
    <div className="border border-[#1E1E1E] bg-[#0F0F10] p-12 text-center">
      <FolderOpen size={32} strokeWidth={1.5} className="mx-auto text-[#D4AF37]" />
      <div className="font-editorial text-2xl mt-4">No projects yet.</div>
      <div className="text-sm text-[#A1A1AA] mt-2 max-w-sm mx-auto">
        Every story starts with a first line. Create a project and StoryFrame will build the rest of the workspace around it.
      </div>
      <Link to="/app/projects?new=1"
        className="mt-6 inline-flex items-center gap-2 bg-[#D4AF37] text-[#0A0A0A] px-6 py-3 text-sm font-medium hover:bg-[#F59E0B] transition-colors"
        data-testid="empty-create-project">
        <Plus size={14} /> Create project
      </Link>
    </div>
  );
}

function ProjectTile({ p }) {
  return (
    <Link to={`/app/projects/${p.id}/script`} className="group block bg-[#141414] border border-[#1E1E1E] p-6 hover:border-[#D4AF37] transition-colors" data-testid={`project-tile-${p.id}`}>
      <div className="uppercase text-[10px] tracking-widest text-[#71717A] mb-3">{p.genre || "Untitled genre"} · {p.format}</div>
      <div className="font-editorial text-2xl mb-3 line-clamp-2">{p.title}</div>
      <div className="text-sm text-[#A1A1AA] line-clamp-3 leading-relaxed">{p.logline || "No logline yet."}</div>
      <div className="mt-6 flex items-center justify-between text-xs text-[#71717A]">
        <span>{p.status}</span>
        <ArrowRight size={14} className="group-hover:text-[#D4AF37] group-hover:translate-x-0.5 transition-all" />
      </div>
    </Link>
  );
}
